import axios from "axios";
import { hideChildModal, hideModal } from "./modal";
import { normalizeId } from "../components/utils/normalize";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
};

/*********/
/* TYPES */
/*********/
const FETCH_CONTACTS = "FETCH_CONTACTS";
const FETCH_CONTACTS_SUCCESS = "FETCH_CONTACTS_SUCCESS";
const FETCH_CONTACTS_ERROR = "FETCH_CONTACTS_ERROR";
const FETCH_NEXT_CONTACTS = "FETCH_NEXT_CONTACTS";
const FETCH_NEXT_CONTACTS_SUCCESS = "FETCH_NEXT_CONTACTS_SUCCESS";
const DELETE_CONTACT = "DELETE_CONTACT";
const CREATE_CONTACT = "CREATE_CONTACT";
const CREATE_CONTACT_SUCCESS = "CREATE_CONTACT_SUCCESS";
const CREATE_CONTACT_ERROR = "CREATE_CONTACT_ERROR";
const UPDATE_CONTACT = "UPDATE_CONTACT";
const UPDATE_CONTACT_SUCCESS = "UPDATE_CONTACT_SUCCESS";
const UPDATE_CONTACT_ERROR = "UPDATE_CONTACT_ERROR";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getContacts = () => async (dispatch) => {
  dispatch(loadContacts());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_API_URL}/contacts?page=1`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.data["hydra:member"].length === 0) {
    dispatch(setError("No contacts found"));
  } else {
    dispatch(setContacts(response.data["hydra:member"]));
    if (
      response.data &&
      response.data["hydra:view"]?.["hydra:next"]
    ) {
      dispatch(getNextContacts(response.data["hydra:view"]["hydra:next"]));
    }
  }
};

export const getNextContacts = (nextUrl) => async (dispatch) => {
  dispatch(loadNextContacts());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_URL}${nextUrl}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  dispatch(setNextContacts(response.data["hydra:member"]));
  if (
    response.data &&
    response.data["hydra:view"]?.["hydra:next"]
  ) {
    dispatch(getNextContacts(response.data["hydra:view"]["hydra:next"]));
  }
};

export const loadContacts = () => ({ type: FETCH_CONTACTS });

export const setContacts = (contacts) => ({
  type: FETCH_CONTACTS_SUCCESS,
  payload: { contacts },
});

export const loadNextContacts = (page) => ({
  type: FETCH_NEXT_CONTACTS,
  payload: page,
});
export const setNextContacts = (contacts) => ({
  type: FETCH_NEXT_CONTACTS_SUCCESS,
  payload: contacts,
});

export const setError = (msg) => ({
  type: FETCH_CONTACTS_ERROR,
  payload: msg,
});

export const deleteContact = () => ({ type: DELETE_CONTACT });

export const removeContact = (contact) => async (dispatch, getState) => {
  dispatch(deleteContact());
  const {
    contacts: { data },
    modal: modal
  } = getState();

  const response = await axios({
    method: "delete",
    url: `${process.env.REACT_APP_URL}${contact["@id"] || `/contacts/${contact.id}`}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.status === 204) {
    const updated = data.filter(
      (c) => c["@id"] !== (contact["@id"] || `/contacts/${contact.id}`)
    );

    dispatch(setContacts(updated));
   
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } else {
    dispatch(setError("Something went wrong deleting contact"));
  }
};

export const createContact = (contact, callback) => async (dispatch, getState) => {
  dispatch({ type: CREATE_CONTACT });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/contacts`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: contact,
    });

    dispatch({
      type: CREATE_CONTACT_SUCCESS,
      payload: response.data,
    });

    if (callback) {
      callback("contacts", [response.data["@id"]]);
    }

    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: CREATE_CONTACT_ERROR,
      payload: error.message || "Failed to create contact",
    });
  }
};

export const updateContact = (contact) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_CONTACT });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "put",
      url: `${process.env.REACT_APP_API_URL}/contacts/${normalizeId(contact["@id"])}`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: contact,
    });

    dispatch({
      type: UPDATE_CONTACT_SUCCESS,
      payload: response.data,
    });
    
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: UPDATE_CONTACT_ERROR,
      payload: error.message || "Failed to update contact",
    });
  }
};

/***********/
/* REDUCER */
/***********/
const contactReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_CONTACTS:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case FETCH_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.contacts,
      };
    case FETCH_CONTACTS_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case FETCH_NEXT_CONTACTS:
      return {
        ...state,
        error: "",
      };
    case FETCH_NEXT_CONTACTS_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [...state.data, ...payload],
      };
    case DELETE_CONTACT:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case CREATE_CONTACT:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case CREATE_CONTACT_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [payload, ...state.data],
      };

    case CREATE_CONTACT_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
     case UPDATE_CONTACT:
       return {
         ...state,
         loading: true,
         error: "",
       };

     case UPDATE_CONTACT_SUCCESS:
       return {
        ...state,
        loading: false,
        data: state.data.map((c) =>
          c["@id"] === payload["@id"] ? payload : c
        ),
      };

     case UPDATE_CONTACT_ERROR:
       return {
         ...state,
         loading: false,
         error: payload,
       };
    default:
      return state;
  }
};

export default contactReducer;